export type GlobalNavigationRefreshResult = 'applied' | 'stale' | 'failed'

/** Identifies the context region to show for a page. */
export interface GlobalNavigationContext {
  path: string
  /** A context object type (`repository`, `organization`, `team`, `user`, or `business`), or `preset`. */
  crumbType: string
  /** The context object's identifier, such as `owner/name` for a repository, or a preset name like `topics`. */
  crumbObjectIdentifier: string
  /** The active local navigation tab. Required for contexts with tabs, and omitted for contexts without them. */
  selectedLink?: string
  /**
   * Path prefix where this context stays valid, such as a repository's `/owner/name`. When history moves to an
   * entry without a stored context and outside this prefix, the page reloads so the server renders its context.
   */
  scope?: string
}

export type GlobalNavigationRefreshOptions = GlobalNavigationContext & {
  navigationCompletion?: Promise<void>
  /** The context currently displayed, cached so returning to it doesn't need a request. */
  currentContext?: GlobalNavigationContext
}

type GlobalNavigationRefreshHandler = (
  options: GlobalNavigationRefreshOptions,
) => Promise<GlobalNavigationRefreshResult>

let registeredGlobalNavigationRefreshHandler: GlobalNavigationRefreshHandler | undefined

export function registerGlobalNavigationRefreshHandler(handler: GlobalNavigationRefreshHandler): () => void {
  registeredGlobalNavigationRefreshHandler = handler
  return () => {
    if (registeredGlobalNavigationRefreshHandler === handler) {
      registeredGlobalNavigationRefreshHandler = undefined
    }
  }
}

export function refreshGlobalNavigation(
  options: GlobalNavigationRefreshOptions,
): Promise<GlobalNavigationRefreshResult> {
  return registeredGlobalNavigationRefreshHandler?.(options) ?? Promise.resolve('failed')
}
